/**
 * Builds the analytics endpoints' responses out of the mock hierarchy, applying the same
 * filters the real endpoints will: keep the task groups matching `taskType` and
 * `managerEmail`, count only the days inside `startDate`–`endDate`, and drop anything
 * with no work left.
 */
import { TaskType } from 'packages/lib/api/annotations/contract/enums';
import {
  CostComparisonPoint,
  CostGroupItem,
  CostGroupResponse,
  CostQuery,
  CostQueryFilters,
  CostSeriesQuery,
  CostSeriesResponse,
  RoleSeriesPoint,
  GroupHierarchyResponse,
  GroupHierarchyType,
  TaskTypesQuery,
  TaskTypesResponse,
} from 'packages/lib/api/annotations-ops/contract';
import {
  taskActivityByDate,
  userActivityByDate,
  userTaskTypes,
} from 'packages/lib/api/annotations-ops/clients/cost/mock/mock-contributor-data';
import {
  addDay,
  DayActivity,
  datesInRange,
  dayActivity,
  emptyDay,
  findGroup,
  groupChain,
  hasFrames,
  MockGroup,
  MockTaskGroup,
  money,
  RangeDay,
  summaryFromActivity,
  taskGroupForTask,
  taskGroupsUnder,
  tasksForDay,
} from 'packages/lib/api/annotations-ops/clients/cost/mock/mock-hierarchy';

// 2026-09-15 Used Cursor (Opus 5) to generate this temporary mock data (to be removed once backend API is available)
// 2026-09-21 Used Cursor (Grok 4.6) to update mocks for Pipelines > Pipeline > Workflow > Sample Group > Task Group, as well as All Requests > Request ID pages
// 2026-10-05 Used Cursor (Claude Opus 5.5 Medium) to add Contributor and Task views

const groupForQuery = ({
  groupType,
  tokenOrId,
}: CostQuery): MockGroup | undefined => findGroup(groupType, tokenOrId);

const matchesFilters = (
  { taskType, isDirectReportWork }: MockTaskGroup,
  { taskTypes, managerEmail }: CostQueryFilters
): boolean =>
  (taskTypes.length === 0 || taskTypes.includes(taskType)) &&
  (!managerEmail || isDirectReportWork);

/** One table row: everything one child of the viewed group did inside the date range. */
const aggregateRow = (
  group: MockGroup,
  query: CostQuery,
  days: RangeDay[]
): CostGroupItem | null => {
  const totals = emptyDay();
  const activeTaskTypes = new Set<TaskType>();

  for (const taskGroup of taskGroupsUnder(group)) {
    if (!matchesFilters(taskGroup, query)) continue;
    for (const day of days) {
      const activity = dayActivity(taskGroup, day);
      if (activity.numTasks === 0) continue;
      activeTaskTypes.add(taskGroup.taskType);
      addDay(totals, activity);
    }
  }

  if (totals.numTasks === 0) return null;

  const includedTaskTypes = [...activeTaskTypes];
  const framed = includedTaskTypes.some(hasFrames);
  const labelingCost = money(totals.labelingCost);
  const qaCost = money(totals.qaCost);
  const auditCost = money(totals.auditCost);

  return {
    token: group.token,
    name: group.name,
    groupType: group.groupType,
    includedTaskTypes,
    labelingCost,
    qaCost,
    auditCost,
    industryCost: money(totals.scaleCost),
    numTasks: totals.numTasks,
    numFrames: framed ? totals.numFrames : null,
    numAnnotations: totals.numAnnotations,
    totalCost: money(labelingCost + qaCost + auditCost),
  };
};

/** A task group's rows are its individual tasks rather than a further level of groups. */
const taskRows = (
  taskGroup: MockTaskGroup,
  query: CostQuery,
  days: RangeDay[]
): CostGroupItem[] => {
  if (!matchesFilters(taskGroup, query)) return [];
  const { taskType } = taskGroup;
  const framed = hasFrames(taskType);

  return days.flatMap((day) => {
    const activity = dayActivity(taskGroup, day);
    return tasksForDay(taskGroup, day, activity).map((task) => ({
      token: task.id,
      name: task.id,
      groupType: 'Task' as const,
      includedTaskTypes: [taskType],
      labelingCost: task.labelingCost,
      qaCost: task.qaCost,
      auditCost: task.auditCost,
      industryCost: task.scaleCost,
      numTasks: 1,
      numFrames: framed ? task.numFrames : null,
      numAnnotations: task.numAnnotations,
      totalCost: money(task.labelingCost + task.qaCost + task.auditCost),
    }));
  });
};

const activityForGroup = (
  group: MockGroup,
  query: CostQuery,
  days: RangeDay[]
): DayActivity => {
  const totals = emptyDay();
  for (const taskGroup of taskGroupsUnder(group)) {
    if (!matchesFilters(taskGroup, query)) continue;
    days.forEach((day) => addDay(totals, dayActivity(taskGroup, day)));
  }
  return totals;
};

/**
 * Mirrors `GET /cost/group`, which returns one row per child of the requested group, or
 * one row per task under it when `includeAllTasks` is set. The summary's `numFrames` is
 * null when no row has frames.
 */
export const buildMockCostGroups = (query: CostQuery): CostGroupResponse => {
  const group = groupForQuery(query);
  if (!group) return { summary: summaryFromActivity(emptyDay()), data: [] };

  const days = datesInRange(query.startDate, query.endDate);
  const data =
    query.includeAllTasks || group.groupType === 'TaskGroup'
      ? taskGroupsUnder(group).flatMap((taskGroup) =>
          taskRows(taskGroup, query, days)
        )
      : group.children
          .map((child) => aggregateRow(child, query, days))
          .filter((row): row is CostGroupItem => row !== null);

  const summary = summaryFromActivity(activityForGroup(group, query, days));
  return {
    summary: {
      ...summary,
      numFrames: data.some((row) => row.numFrames !== null)
        ? summary.numFrames
        : null,
    },
    data,
  };
};

/**
 * How each series turns a day's totals into a point. Comparison swaps the role
 * breakdown for iMerit labeling spend against the industry (Scale) benchmark.
 */
const METRIC_POINT = {
  totalCost: (date: string, totals: DayActivity): RoleSeriesPoint => ({
    date,
    labeling: money(totals.labelingCost),
    qa: money(totals.qaCost),
    audit: money(totals.auditCost),
  }),
  totalTasks: (date: string, totals: DayActivity): RoleSeriesPoint => ({
    date,
    labeling: totals.numTasks,
    qa: totals.numQATasks,
    audit: totals.numAuditTasks,
  }),
  totalHours: (date: string, totals: DayActivity): RoleSeriesPoint => ({
    date,
    labeling: money(totals.labelingHours),
    qa: money(totals.qaHours),
    audit: money(totals.auditHours),
  }),
  comparison: (date: string, totals: DayActivity): CostComparisonPoint => ({
    date,
    labeling: money(totals.labelingCost),
    industry: money(totals.scaleCost),
  }),
};

/** One point per day in the range, summing the task groups' filtered activity. */
const groupActivityByDate = (
  group: MockGroup | undefined,
  filters: CostQueryFilters
): { date: string; totals: DayActivity }[] => {
  const taskGroups = group
    ? taskGroupsUnder(group).filter((taskGroup) =>
        matchesFilters(taskGroup, filters)
      )
    : [];
  return datesInRange(filters.startDate, filters.endDate).map((day) => {
    const totals = emptyDay();
    taskGroups.forEach((taskGroup) =>
      addDay(totals, dayActivity(taskGroup, day))
    );
    return { date: day.date, totals };
  });
};

/**
 * Mirrors `GET /cost/time-series`. Every series is returned together. Group levels
 * and contributors get one point per day in the requested range, summing the same
 * filtered activity that feeds the table; days with no matching work still appear as
 * zeroes so the chart's axis matches the date picker. A task gets one point per day a
 * phase was completed. The fixture only records completed work, so unlike the real
 * endpoint its hours exclude in-progress tasks.
 */
export const buildMockCostSeries = (
  query: CostSeriesQuery
): CostSeriesResponse => {
  const activity = (() => {
    switch (query.type) {
      case 'Task':
        return taskActivityByDate(query.tokenOrId);
      case 'Contributor':
        return userActivityByDate({ ...query, userEmail: query.tokenOrId });
      case 'AllPipelines':
      case 'AllRequests':
        return groupActivityByDate(findGroup(query.type), query);
      default:
        return groupActivityByDate(
          findGroup(query.type, query.tokenOrId),
          query
        );
    }
  })();

  return {
    data: {
      totalCost: activity.map(({ date, totals }) =>
        METRIC_POINT.totalCost(date, totals)
      ),
      totalTasks: activity.map(({ date, totals }) =>
        METRIC_POINT.totalTasks(date, totals)
      ),
      totalHours: activity.map(({ date, totals }) =>
        METRIC_POINT.totalHours(date, totals)
      ),
      comparison: activity.map(({ date, totals }) =>
        METRIC_POINT.comparison(date, totals)
      ),
    },
  };
};

/** Mirrors `GET /group/hierarchy`, the chain of groups a page's breadcrumbs walk back up. */
export const buildMockGroupHierarchy = (
  groupType: GroupHierarchyType,
  token?: string
): GroupHierarchyResponse => {
  if (groupType === 'Task') {
    const taskGroup = token ? taskGroupForTask(token) : undefined;
    return {
      data: taskGroup
        ? groupChain(taskGroup.token).map((group) => ({
            groupType: group.groupType,
            token: group.token,
            name: group.name,
          }))
        : [],
    };
  }

  if (groupType === 'AllPipelines' || groupType === 'AllRequests' || !token)
    return { data: [] };

  if (groupType === 'RequestId') {
    const request = findGroup('RequestId', token);
    return {
      data: request
        ? [
            {
              groupType: 'RequestId',
              token: request.token,
              name: request.name,
            },
          ]
        : [],
    };
  }

  return {
    data: groupChain(token).map((group) => ({
      groupType: group.groupType,
      token: group.token,
      name: group.name,
    })),
  };
};

const groupTaskTypes = (group: MockGroup | undefined): TaskType[] =>
  group ? taskGroupsUnder(group).map((taskGroup) => taskGroup.taskType) : [];

/**
 * Mirrors `GET /tasktypes`. This deliberately ignores cost-page filters: the menu must
 * contain every task type available on the page before a Task Type filter is applied.
 */
export const buildMockTaskTypes = (
  query: TaskTypesQuery
): TaskTypesResponse => {
  const taskTypes = (() => {
    switch (query.type) {
      case 'AllPipelines':
      case 'AllRequests':
        return groupTaskTypes(findGroup(query.type));
      case 'Contributor':
        return userTaskTypes(query.tokenOrId);
      default:
        return groupTaskTypes(findGroup(query.type, query.tokenOrId));
    }
  })();
  return [...new Set(taskTypes)].sort();
};
